import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CaretDown, CaretRight, UsersThree } from '@phosphor-icons/react';

function roleLabel(role) {
  const r = String(role || '').toLowerCase();
  if (r === 'manager') return 'Manager';
  if (r === 'lead') return 'Lead';
  if (r === 'member') return 'Member';
  return role ? String(role) : null;
}

/**
 * Product → members workload panel with pipeline drill-down.
 * Ranked list layout (avoids cramped overlapping grids in the chart card).
 */
export default function ProductTeamsWidget({ items = [], title = 'Team by product' }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(() => new Set());

  const maxActive = useMemo(
    () => Math.max(1, ...items.map((p) => Number(p.active_total) || 0)),
    [items],
  );

  const toggle = (productId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  const openPipeline = (productId) => {
    navigate(`/dashboard/tickets/pipeline?product_id=${encodeURIComponent(productId)}`);
  };

  const openMember = (productId, userId) => {
    // Same Product 360° page as the 360° button, pre-filtered to this person.
    const sp = new URLSearchParams({
      product_id: productId,
      assigned_to: userId,
      view: 'active',
    });
    navigate(`/dashboard/tickets/pipeline?${sp.toString()}`);
  };

  if (!items.length) {
    return (
      <div className="wd-chart-card wd-soft-card is-auto-height" data-testid="product-teams-widget">
        <p className="wd-chart-title">{title}</p>
        <p className="wd-chart-subtitle">No product teams in your scope yet.</p>
      </div>
    );
  }

  return (
    <div className="wd-chart-card wd-soft-card is-auto-height" data-testid="product-teams-widget">
      <p className="wd-chart-title">{title}</p>
      <p className="wd-chart-subtitle">
        Active workload by product · click person or 360° for product view
      </p>
      <div className="wd-chart-body is-scroll">
        <div className="wd-product-teams">
        {items.map((product) => {
          const open = expanded.has(product.product_id);
          const active = Number(product.active_total) || 0;
          const closed = Number(product.closed_total) || 0;
          const members = Number(product.member_count) || 0;
          const pct = Math.round((active / maxActive) * 100);
          const managerName = product.manager?.name;

          return (
            <div key={product.product_id} className={`wd-product-team${open ? ' is-open' : ''}`}>
              <div className="wd-product-team-row">
                <button
                  type="button"
                  className="wd-product-team-toggle"
                  onClick={() => toggle(product.product_id)}
                  aria-expanded={open}
                >
                  {open ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
                  <UsersThree size={14} weight="bold" className="wd-product-team-icon" />
                  <span className="wd-product-team-heading">
                    <span className="wd-product-team-name">{product.product_name || product.product_key}</span>
                    <span className="wd-product-team-manager">
                      {managerName ? `Manager: ${managerName}` : 'No manager assigned'}
                    </span>
                  </span>
                </button>
                <button
                  type="button"
                  className="wd-product-team-pipeline"
                  onClick={() => openPipeline(product.product_id)}
                >
                  360°
                </button>
              </div>

              <div className="wd-product-team-bar-meta">
                <span>{members} members</span>
                <span>{active} active</span>
                <span>{closed} closed</span>
              </div>
              <div className="wd-product-team-track" aria-hidden>
                <div className="wd-product-team-fill" style={{ width: `${pct}%` }} />
              </div>

              {open && (
                <ul className="wd-product-team-members">
                  {(product.members || []).map((m) => {
                    const role = roleLabel(m.org_role);
                    return (
                      <li key={m.user_id}>
                        <button
                          type="button"
                          className="wd-product-team-member"
                          onClick={() => openMember(product.product_id, m.user_id)}
                          title="Open member pipeline"
                        >
                          <div className="wd-ptm-top">
                            <span className="wd-ptm-name-wrap">
                              <span className="wd-ptm-name">{m.name}</span>
                              {role && <span className="wd-ptm-role">{role}</span>}
                            </span>
                            <span className="wd-ptm-closed">{m.closed_count ?? 0} closed</span>
                          </div>
                          <div className="wd-ptm-doing">{m.doing || 'Idle'}</div>
                          <div className="wd-ptm-counts">
                            <span>{m.open_count ?? 0} open</span>
                            <span>{m.in_progress_count ?? 0} in progress</span>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                  {!product.members?.length && (
                    <li className="wd-ptm-empty">No members on this product team.</li>
                  )}
                </ul>
              )}
            </div>
          );
        })}
        </div>
      </div>
    </div>
  );
}
